import { create } from "@bufbuild/protobuf";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tagServiceClient } from "@/connect";
import {
  CreateTagRequestSchema,
  FindOrCreateTagRequestSchema,
  ListTagsRequestSchema,
  UpdateTagRequestSchema,
} from "@/types/proto/api/v1/tag_service_pb";

// Query keys for tag-related queries
export const TAG_QUERY_KEY = "tags";

interface UseTagsOptions {
  enabled?: boolean;
}

/**
 * useTags fetches all tags for the current user
 */
export function useTags(options: UseTagsOptions = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: [TAG_QUERY_KEY],
    queryFn: async () => {
      const request = create(ListTagsRequestSchema, {});
      const response = await tagServiceClient.listTags(request);
      return response.tags;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (renamed from cacheTime)
  });
}

/**
 * useCreateTag creates a new tag
 */
export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ displayName, colorToken }: { displayName: string; colorToken: string }) => {
      const request = create(CreateTagRequestSchema, {
        tag: {
          displayName,
          colorToken,
        },
      });
      return await tagServiceClient.createTag(request);
    },
    onSuccess: () => {
      // Invalidate tags query to refetch
      queryClient.invalidateQueries({ queryKey: [TAG_QUERY_KEY] });
    },
  });
}

/**
 * useFindOrCreateTag finds an existing tag by display name, or creates a new one
 */
export function useFindOrCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ displayName, colorToken }: { displayName: string; colorToken?: string }) => {
      const request = create(FindOrCreateTagRequestSchema, {
        displayName,
        colorToken: colorToken || "primary",
      });
      return await tagServiceClient.findOrCreateTag(request);
    },
    onSuccess: () => {
      // Invalidate tags query to refetch
      queryClient.invalidateQueries({ queryKey: [TAG_QUERY_KEY] });
    },
  });
}

/**
 * useUpdateTag updates an existing tag
 */
export function useUpdateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tagName, displayName, colorToken }: { tagName: string; displayName?: string; colorToken?: string }) => {
      const request = create(UpdateTagRequestSchema, {
        tag: {
          name: tagName,
          displayName: displayName,
          colorToken: colorToken,
        },
      });
      return await tagServiceClient.updateTag(request);
    },
    onSuccess: () => {
      // Invalidate tags query to refetch
      queryClient.invalidateQueries({ queryKey: [TAG_QUERY_KEY] });
    },
  });
}

/**
 * useDeleteTag deletes a tag (soft delete)
 */
export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tagName: string) => {
      return await tagServiceClient.deleteTag({ name: tagName });
    },
    onSuccess: () => {
      // Invalidate tags query to refetch
      queryClient.invalidateQueries({ queryKey: [TAG_QUERY_KEY] });
    },
  });
}
